import { Alert, Box, Button, CircularProgress, Divider, Grid, Link, Typography } from "@mui/material"
import moment from "moment"
import { useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import successBanner from "../../../../assets/payment_success_banner.svg"
import { getPayment, OrderRequest, PaymentResponse, ServiceType, PaymentInfo, paymentInfoFromPaymentResponse, extractPaymentResponseContent } from "../../../../services/ntuc/memberService/payment"
import { AcknowledgementPageProps } from "../../../../views/signup-flow/AcknowledgementPage/AcknowledgementPage"
import { SignupRoute } from "../../../../views/signup-flow/SignupFlow/SignupFlow"
import PaymentAcknowledgementBanner from "../../PaymentAcknowledgementBanner"
import { OrderStatus } from "../../../../services/ntuc/memberService/payment"
import { DeviceType } from "../../../../services/ntuc/memberService/oaMembership"
import { postCreateOrder } from "../../../../services/ntuc/memberService/payment"
import { redirectToPaymentGateway } from "../../../../services/ntuc/paymentService/payment"
import { LoadingButton } from "@mui/lab"
import PaymentInfoError from "../../PaymentInfoError/PaymentInfoError"
import { getTrustBanner } from "../../../../services/ntuc/contentService/trustBanner"
import TrusBanner from "../../TrustBanner"

const APP_LINK = process.env.REACT_APP_UNIVERSAL_LINK
const SURVEY_LINK = process.env.REACT_APP_SURVEY_LINK || `https://survey.alchemer.com/s3/7317981/Share-with-us-your-sign-up-experience-for-NTUC-Membership`

const PaymentSuccessPage = (props: AcknowledgementPageProps) => {
    const { t } = useTranslation([`acknowledgement`, `common`])
    const { orderId, campaign, onNavigateTo } = props

    const [loading, setLoading] = useState<boolean>(false)
    const [paymentResponse, setPaymentResponse] = useState<PaymentResponse>()
    const [order, setOrder] = useState<PaymentInfo>()
    const [hasError, setHasError] = useState<boolean>(false)
    const [topTrustBanner, setTopTrustBanner] = useState(``)
    const [bottomTrustBanner, setBottomTrustBanner] = useState(``)
    const [topTrustBannerUrl, setTopTrustBannerUrl] = useState(``)
    const [bottomTrustBannerUrl, setBottomTrustBannerUrl] = useState(``)
    const buildAssetUrl = (location: string) => {
        return window.myNtucConfig.assetsBaseUrl + location
    }

    const DECIMALS = 2

    useEffect(() => {
        getTrustBanner().then(response => response.data.content).then(content => {
            return content
        })
            .then(trustBanner => {
                if(trustBanner.topImage)
                    setTopTrustBanner(buildAssetUrl(trustBanner.topImage))
                if(trustBanner.bottomImage)
                    setBottomTrustBanner(buildAssetUrl(trustBanner.bottomImage))
                setTopTrustBannerUrl(trustBanner.topBannerUrl)
                setBottomTrustBannerUrl(trustBanner.bottomBannerUrl)
            })
    }, [])

    useEffect(() => {
        if (orderId) {
            getPayment(orderId)
                .then(extractPaymentResponseContent)
                .then(interceptPaymentResponse)
                .then(paymentInfoFromPaymentResponse)
                .then(setOrder)
                .catch(error => {
                    setHasError(true)
                    console.error(error.message)
                })
        }
    }, [orderId])

    const handleTryAgainClick = () => {
        const orderRequest: OrderRequest = {
            feeType: parseInt(paymentResponse!.membershipPayment.feeType),
            endDate: paymentResponse!.membershipPayment.endDate,
            startDate: paymentResponse!.membershipPayment.startDate,
            serviceType: ServiceType.SIGN_UP,
            noOfMonths: paymentResponse!.membershipPayment.noOfMonths,
            amount: `${paymentResponse!.payment.orderAmount}`,
            isOneTimePayment: true,
            isRecurringPayment: false,
            membershipId: parseInt(paymentResponse!.membershipPayment.membershipId),
            deviceType: DeviceType.WEB,
        }

        setLoading(true)
        postCreateOrder(orderRequest)
            .then(response => response.data.content.clientOrderId)
            .then(redirectToPaymentGateway)
            .catch(error => {
                setHasError(true)
                console.error(error.response.data.content)
            })
            .then(() => setLoading(false))
    }

    const handleHomeClick = () => {
        if (window.myNtucConfig.signupBackToHomeHomeUrl != undefined) {
            window.location.replace(window.myNtucConfig.signupBackToHomeHomeUrl)
        } else {
            onNavigateTo(SignupRoute.Disclaimer)
        }
    }

    const handleReturnClick = () => {
        if (campaign?.callback) {
            window.location.replace(campaign.callback)
        }
    }

    const isCampaignDefined = () => {
        return campaign && campaign.code && campaign.callback && campaign.app
    }

    const interceptPaymentResponse = (paymentResponse: PaymentResponse): PaymentResponse => {
        setPaymentResponse(paymentResponse)
        return paymentResponse
    }

    const hasSUGStatusTrue = () : boolean => paymentResponse != undefined && paymentResponse.campaignSUGStatus

    const hasUpfrontPaymentFailed = (): boolean => order != undefined && order.orderStatus == OrderStatus.SEMI_SUCCESS_RECURRING

    const hasRecurringPaymentFailed = (): boolean => order != undefined && order.orderStatus == OrderStatus.SEMI_SUCCESS_ONE_TIME

    const displayedAmount = (): string => hasUpfrontPaymentFailed() ? `Failed` : ``.concat(`$`, order!.amount.toFixed(DECIMALS))

    const OrderContent = () => {
        if (order == undefined) {
            return (
                <Grid container item justifyContent="center" xs={12}>
                    <CircularProgress />
                </Grid>
            )
        } else {
            return (
                <Grid container item xs={12}>
                    <Grid item justifyContent="start" xs={12}>
                        <Divider />
                        <Typography pt={3} mb={3} variant="h5">{t(`success.payments.title`)}</Typography>
                        <Grid container item xs={12}>
                            <Grid item xs={10}>
                                <Box>
                                    <Typography>{t(`success.payments.description`, { count: order?.month })}</Typography>
                                    <Typography my={2} hidden={hasUpfrontPaymentFailed() || order?.amount === 0}
                                        color="text.secondary">{moment(order?.startDate).format(`DD MMM YYYY`)} - {moment(order?.endDate).format(`DD MMM YYYY`)}</Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography textAlign="end">{displayedAmount()}</Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item md={12} hidden={!hasUpfrontPaymentFailed()}>
                        <Grid item md={12} hidden={!hasSUGStatusTrue()}>
                            <Alert severity="error">
                                <Trans t={t}>success.error_upfront_payment</Trans>
                            </Alert>
                        </Grid>
                        <Grid item md={12} hidden={hasSUGStatusTrue()}>
                            <Alert severity="error">
                                <Trans t={t}>success.error_upfront_payment_noSUG</Trans>
                            </Alert>
                        </Grid>
                    </Grid>
                    <Grid item md={12} hidden={!hasRecurringPaymentFailed()}>
                        <Alert severity="error">
                            <Trans t={t}>success.error_recurring_payment</Trans>
                        </Alert>
                    </Grid>
                    <Grid container item justifyContent="start" xs={12}>
                        <Divider />
                        <Grid container item py={3} xs={12}>
                            <Grid item xs={10}>
                                <Typography mb={3} variant="h5">{t(`success.payments.total`)}</Typography>
                            </Grid>
                            <Grid item xs={2}>
                                <Typography textAlign="end">{displayedAmount()}</Typography>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            )
        }
    }

    if (hasError) {
        return <PaymentInfoError onHomeClick={handleHomeClick} />
    }

    return (
        <Grid rowSpacing={3} container justifyContent="center" padding={2}>
            <PaymentAcknowledgementBanner bannerUrl={successBanner} alt="Payment success banner" />
            <Grid container item justifyContent="center" xs={12}>
                <Typography textAlign="center" variant="h2">{t(`success.title`)}</Typography>
            </Grid>
            <Grid item justifyContent="center" xs={12}>
                <Typography textAlign="center">{t(`success.body`)}</Typography>
                <Typography textAlign="center">
                    <Link href={APP_LINK} target="_blank" rel="noreferrer" textAlign="center">
                        {t(`success.e-card`)}
                    </Link>
                </Typography>
                <Typography textAlign="center">
                    <Button sx={{marginTop: `1rem`}} href={SURVEY_LINK} variant="contained" color="primary" size="large">
                        Share your sign-up experience
                    </Button>
                </Typography>
            </Grid>
            <Grid item justifyContent="center" xs={12}>
                <Typography color="text.secondary" textAlign="center">{t(`success.reference`)}</Typography>
                <Typography textAlign="center" data-testid="reference">{orderId}</Typography>
            </Grid>
            <OrderContent />
            {topTrustBanner && <TrusBanner bannerUrl={topTrustBannerUrl} alt="Top trust banner" bannerImage={topTrustBanner}/>}
            {bottomTrustBanner && <TrusBanner bannerUrl={bottomTrustBannerUrl} alt="Bottom trust banner" bannerImage={bottomTrustBanner}/>}
            <Grid container item xs={12} justifyContent="center" alignItems="center" columnSpacing={2} rowSpacing={3}>
                <Grid item xs={12} md={3} textAlign="center" hidden={!hasUpfrontPaymentFailed()}>
                    <LoadingButton loading={loading} disabled={loading} onClick={handleTryAgainClick} variant="contained" color="primary" size="large">
                        {t(`buttons.try_again`)}
                    </LoadingButton>
                </Grid>
                <Grid item xs={12} md={3} textAlign="center" hidden={!isCampaignDefined()}>
                    <Button onClick={handleReturnClick} variant="outlined" color="primary" size="large">
                        {t(`buttons.return_to`, { app_name: campaign?.app, interpolation: { escapeValue: false } })}
                    </Button>
                </Grid>
                <Grid item xs={12} md={3} textAlign="center">
                    <Button onClick={handleHomeClick} variant="contained" color="primary" size="large">
                        {t(`buttons.home`)}
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default PaymentSuccessPage
